import { NextPage } from 'next'
import { useContext, useEffect, useRef, useState } from 'react'
import { useApolloClient, useLazyQuery, useMutation } from '@apollo/client'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faHeart } from '@fortawesome/free-regular-svg-icons'
import {
  faHeart as solidHeart,
  faSearch
} from '@fortawesome/free-solid-svg-icons'
import styles from './_songRequestEntriesPage.module.scss'
import Layout from '../../components/Layout'
import { t } from '../../utils/i18n'
import { SONG_REQUEST_ENTRIES_QUERY } from '../../gql/queries'
import { useAuth } from '../../utils/auth'
import { errorMessage } from '../../utils/errorMessage'
import {
  CREATE_SONG_REQUEST_MUTATION,
  TOGGLE_VOTE_TO_SONG_REQUEST_MUTATION
} from '../../gql/mutations'
import { SONG_REQUEST_ENTRY_FIELDS_FRAGMENT } from '../../gql/fragments'
import Link from 'next/link'
import { songPath } from '../../utils/routeHelpers'
import { useForm } from 'react-hook-form'
import { ErrorMessage } from '@hookform/error-message'
import { staticProps } from '../../utils/createApolloClient'
import classnames from 'classnames'
import { ModalsContext } from '../../utils/modals'
import useErrorHandling from '../../utils/hooks/useErrorHandling'
import useScrollLoading from '../../utils/hooks/useScrollLoading'
import LoadingRing from '../../components/LoadingRing'
import { SongRequestEntry, Song } from '../../__generated__/graphql'

interface SongRequestEntriesPageProps {
  songRequestEntriesSsr: SongRequestEntry[]
}

interface SongRequestEntriesState {
  result: SongRequestEntry[]
  q: string
  page: number
  perPage: number
}

interface ItemProps {
  songRequest: SongRequestEntry
  isSignedIn: boolean
  isAdmin: boolean
  onClick: () => void
}

interface BasicItemContentProps {
  songRequestEntry: SongRequestEntry
  isSignedIn: boolean
  isAdmin: boolean
  toggleVote: () => void
  onClick: () => void
}

interface ItemWithSongContentProps {
  songRequestEntry: SongRequestEntry
  song: Song
}

interface SongRequestBodyProps {
  songRequestEntry: SongRequestEntry
}

const Header: React.FC = () => (
  <div className="container">
    <div className={styles.songRequestEntriesContainer}>
      <h1 className={styles.songRequestEntriesTitle}>{t('Song Request')}</h1>
      <h5 className={styles.songRequestEntriesSubTitle}>
        {t(
          'Enter the title of any song you’d like to see in our library. If the song is already listed, please vote for it!'
        )}
      </h5>
    </div>
  </div>
)

const BasicItemContent: React.FC<BasicItemContentProps> = ({
  songRequestEntry,
  isSignedIn,
  isAdmin,
  toggleVote,
  onClick
}) => (
  <>
    <div className={styles.requestWrapper}>
      {isSignedIn && isAdmin ? (
        <Link
          href={`${process.env.NEXT_PUBLIC_ADMIN_DOMAIN}/admin/song_requests/${songRequestEntry.entryId}`}
          prefetch={false}
          target="_blank"
        >
          <SongRequestBody songRequestEntry={songRequestEntry} />
        </Link>
      ) : (
        <SongRequestBody songRequestEntry={songRequestEntry} />
      )}
    </div>
    <div
      className={styles.requestVotes}
      onClick={isSignedIn ? () => toggleVote() : () => onClick()}
    >
      <div>
        {isSignedIn && songRequestEntry.votedByUser ? (
          <FontAwesomeIcon
            icon={solidHeart}
            className={styles.requestVotesPink}
          />
        ) : (
          <FontAwesomeIcon icon={faHeart} />
        )}
      </div>
      <div>{songRequestEntry?.votesCount}</div>
    </div>
  </>
)

const SongRequestBody: React.FC<SongRequestBodyProps> = ({
  songRequestEntry
}) => (
  <div className={styles.requestNotations}>
    <p className={styles.requestTitle}>{songRequestEntry?.title}</p>
    <div className={styles.requestContainer}>
      <div>
        <span className={styles.requestDetails}>{t('Artist')}:</span>
        <span className={styles.requestArtist}>{songRequestEntry?.artist}</span>
      </div>
      <div>
        <span className={styles.requestDetails}>{t('Album')}:</span>
        <span>{songRequestEntry?.album}</span>
      </div>
    </div>
  </div>
)

const ItemWithSongContent: React.FC<ItemWithSongContentProps> = ({
  songRequestEntry,
  song
}) => (
  <>
    <Link href={songPath(song)}>
      <div className={styles.requestWrapper}>
        <div className={styles.requestNote}>
          <p>
            <span className={styles.requestNoteMain}>
              {t('We’ve already added this song to our library. ')}
            </span>
            <span className={styles.requestNoteBold}>{t('Check it out!')}</span>
          </p>
        </div>
        <div
          className={classnames(
            styles.requestNotations,
            styles.requestNotationsWithSong
          )}
        >
          <p className={styles.requestTitle}>{songRequestEntry.title}</p>
          <div className={styles.requestContainer}>
            <div>
              <span className={styles.requestDetails}>{t('Artist')}:</span>
              <span className={styles.requestArtist}>
                {songRequestEntry.artist}
              </span>
            </div>
            <div>
              <span className={styles.requestDetails}>{t('Album')}:</span>
              <span>{songRequestEntry.album}</span>
            </div>
          </div>
        </div>
      </div>
    </Link>
    <Link
      href={songPath(song)}
      className={classnames(styles.requestVotes, styles.requestArrow)}
    >
      <div>
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="28"
          height="50"
          viewBox="0 0 28 50"
        >
          <g>
            <g>
              <path
                fill="none"
                strokeMiterlimit="20"
                strokeWidth="4"
                d="M1.61 1.5v0l23.542 23.543v0L1.61 48.586v0"
              />
            </g>
          </g>
        </svg>
      </div>
    </Link>
  </>
)

const Item: React.FC<ItemProps> = ({
  songRequest,
  isSignedIn,
  isAdmin,
  onClick
}) => {
  const { id, entryId, song } = songRequest
  const client = useApolloClient()
  const songRequestEntry = client.readFragment<SongRequestEntry>({
    id: `SongRequestEntry:${id}`,
    fragment: SONG_REQUEST_ENTRY_FIELDS_FRAGMENT
  })
  const errorHandling = useErrorHandling()

  const [toggleVote] = useMutation(TOGGLE_VOTE_TO_SONG_REQUEST_MUTATION, {
    onError: errorHandling,
    variables: { songRequestId: entryId },
    update: (cache, result) => {
      const { votesCount, votedByUser } =
        result.data.toggleVoteToSongRequest.songRequestEntry
      const data = cache.readFragment<SongRequestEntry>({
        id: `SongRequestEntry:${id}`,
        fragment: SONG_REQUEST_ENTRY_FIELDS_FRAGMENT
      })

      const prevData = data || songRequest

      if (!prevData) return
      cache.writeFragment<SongRequestEntry>({
        id: `SongRequestEntry:${id}`,
        fragment: SONG_REQUEST_ENTRY_FIELDS_FRAGMENT,
        data: {
          ...prevData,
          votesCount,
          votedByUser
        }
      })
    }
  })

  return (
    <div className={styles.request}>
      {songRequest.song ? (
        <ItemWithSongContent
          song={song}
          songRequestEntry={songRequestEntry || songRequest}
        />
      ) : (
        <BasicItemContent
          toggleVote={toggleVote}
          songRequestEntry={songRequestEntry || songRequest}
          isSignedIn={isSignedIn}
          isAdmin={isAdmin}
          onClick={onClick}
        />
      )}
    </div>
  )
}

const SongRequestEntriesPage: NextPage<SongRequestEntriesPageProps> = ({
  songRequestEntriesSsr
}) => {
  const initialSongRequestEntries: SongRequestEntriesState = {
    result: songRequestEntriesSsr,
    q: '',
    page: 1,
    perPage: 10
  }
  const [songRequestEntries, setSongRequestEntries] =
    useState<SongRequestEntriesState>(initialSongRequestEntries)
  const { isAuthenticated, isAdmin } = useAuth()

  const { openModal, closeModal } = useContext(ModalsContext)

  const openSignInModal = () => {
    openModal({
      name: 'SignIn',
      props: {
        onClose: closeModal
      },
      modalTitle: t('Sign In')
    })
  }

  const sortWeight = (entry: SongRequestEntry): number => {
    return entry.song ? 1 : 0
  }

  const [performSearch, { loading }] = useLazyQuery(
    SONG_REQUEST_ENTRIES_QUERY,
    {
      onCompleted: (data) => {
        let newEntries = data.songRequestEntries.slice() as SongRequestEntry[]
        if (songRequestEntries.page > 1) {
          newEntries = songRequestEntries.result.concat(newEntries)
        }
        setSongRequestEntries({
          ...songRequestEntries,
          result: newEntries.sort((a, b) => sortWeight(b) - sortWeight(a))
        })
      }
    }
  )

  const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSongRequestEntries({
      ...songRequestEntries,
      q: event.target.value,
      page: initialSongRequestEntries.page
    })
    performSearch({
      variables: {
        q: event.target.value,
        page: initialSongRequestEntries.page
      }
    })
  }

  const loadNewDataInBottomPosition = (songRequestData) => {
    if (
      songRequestData.result?.length > 0 &&
      songRequestData.result?.length >= songRequestData.perPage
    ) {
      const page = songRequestData.page + 1
      setSongRequestEntries({
        ...songRequestData,
        page
      })
      performSearch({
        variables: {
          q: songRequestData.q,
          page
        }
      })
    }
  }

  const songsListContainerRef = useRef<HTMLDivElement>(null)

  useScrollLoading(
    songsListContainerRef,
    () => loadNewDataInBottomPosition(songRequestEntries),
    [songRequestEntries?.result]
  )

  useEffect(() => {
    if (isAuthenticated) performSearch()
  }, [isAuthenticated])

  const [createSongRequest] = useMutation(CREATE_SONG_REQUEST_MUTATION, {
    onError: (error) => {
      const message = errorMessage(error)
      setError('common', { type: 'server', message })
    },
    onCompleted: (data) => {
      setSongRequestEntries({
        ...songRequestEntries,
        result: [data.createSongRequest.songRequestEntry as SongRequestEntry]
      })
    }
  })

  const { register, handleSubmit, errors, setError, clearErrors } = useForm({
    mode: 'onChange'
  })

  const onSubmit = (data) => {
    createSongRequest({ variables: data })
  }

  const renderField = ({ fieldName, fieldType = 'text' }) => {
    return (
      <div className={`${errors[fieldName] && styles.formDanger}`}>
        <input
          ref={register({ required: t('Is required') })}
          type={fieldType}
          name={fieldName}
          placeholder={t(fieldName)}
          onChange={() => {
            clearErrors('common')
          }}
        />
        <ErrorMessage
          errors={errors}
          name={fieldName}
          render={({ message }) => (
            <p className={styles.errorInside}>{message}</p>
          )}
        />
      </div>
    )
  }

  return (
    <Layout
      pageTitle={t('WorshipOnline - Song Requests')}
      pageDescription={t(
        'Request any song you would like to see added to our library of tutorials. The songs with the most votes get added first!'
      )}
    >
      <div className={styles.songRequestsPage}>
        <Header />
        <div className={styles.songRequestEntriesSection}>
          <div className="container">
            <div
              ref={songsListContainerRef}
              className={styles.songRequestEntriesContainer}
            >
              <div
                className={
                  songRequestEntries.result.length == 0 && isAuthenticated
                    ? `${styles.songRequestEntriesSearch} ${styles.songRequestEntriesSearchWithForm}`
                    : styles.songRequestEntriesSearch
                }
              >
                <input
                  type="text"
                  onChange={handleSearch}
                  autoComplete="off"
                  placeholder={t('Song, Album or Artist name...')}
                />
                <span>
                  <FontAwesomeIcon icon={faSearch} />
                </span>
              </div>
              {songRequestEntries.result.length == 0 && isAuthenticated ? (
                <form
                  className={`${styles.form} ${
                    errors.common && styles.formDangerForm
                  }`}
                  onSubmit={handleSubmit(onSubmit)}
                >
                  {errors.common && (
                    <p className={styles.error}>{errors.common.message}</p>
                  )}
                  <p className={styles.formTitle}>{t('New Request')}</p>
                  <p className={styles.formSubTitle}>
                    {t(
                      'Looks like we don’t have this song in our library and it’s not on the queue list either.'
                    )}
                  </p>
                  {renderField({ fieldName: 'title' })}
                  {renderField({ fieldName: 'artist' })}
                  {renderField({ fieldName: 'album' })}
                  <button type="submit" className={styles.formButton}>
                    {t('submit')}
                  </button>
                </form>
              ) : (
                <>
                  {songRequestEntries.result.map((songRequest) => (
                    <Item
                      key={songRequest.id}
                      songRequest={songRequest}
                      isSignedIn={isAuthenticated}
                      isAdmin={isAdmin}
                      onClick={openSignInModal}
                    />
                  ))}
                  {loading && <LoadingRing />}
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </Layout>
  )
}

export const getStaticProps = staticProps({
  additionalProps: async ({ client }) => {
    const {
      data: { songRequestEntries }
    } = await client.query({ query: SONG_REQUEST_ENTRIES_QUERY })

    return {
      songRequestEntriesSsr: songRequestEntries || null
    }
  }
})

export default SongRequestEntriesPage
